import { useContext, type CSSProperties } from 'react';
import { HolderOutlined, UpOutlined } from '@ant-design/icons';
import { TfiRulerAlt } from 'react-icons/tfi';
import { UserContext } from '@/state/contexts';
import { darkColors } from '@/theme/colors';
import { useImageAnnotations } from './hooks/useImageAnnotations';
import { useAnnotationVisibility } from './hooks/useAnnotationVisibility';
import { useListPlacement } from './hooks/useListPlacement';
import styles from './MeasurementList.module.css';

interface MeasurementListProps {
  dicomUuid: string;
  stackImageId: string | null;
  /** Zero-based frame the pane is showing. */
  currentFrame: number;
}

export function MeasurementList({
  dicomUuid,
  stackImageId,
  currentFrame,
}: MeasurementListProps) {
  const { user } = useContext(UserContext);
  const userId = user?.id ?? 'anonymous';
  const rows = useImageAnnotations(dicomUuid, stackImageId);
  const { toggle } = useAnnotationVisibility(rows, userId);
  const shown = rows.length > 0;
  const { panelRef, placement, onDragStart, toggleCollapsed } =
    useListPlacement(shown);

  if (!shown) return null;

  return (
    <div
      ref={panelRef}
      className={styles.list}
      style={{ left: placement.x, top: placement.y }}
      data-testid="measurement-list"
      data-collapsed={placement.collapsed || undefined}
    >
      <div className={styles.header} onPointerDown={onDragStart}>
        {!placement.collapsed && (
          <>
            <HolderOutlined className={styles.grip} aria-hidden />
            <span className={styles.title}>Measurements</span>
          </>
        )}
        <button
          type="button"
          className={styles.collapse}
          onClick={toggleCollapsed}
          aria-label={
            placement.collapsed
              ? 'Expand measurement list'
              : 'Collapse measurement list'
          }
          aria-expanded={!placement.collapsed}
        >
          {placement.collapsed ? (
            <TfiRulerAlt aria-hidden />
          ) : (
            <UpOutlined aria-hidden />
          )}
        </button>
      </div>
      {!placement.collapsed && (
        <ul className={styles.rows}>
          {rows.map((row) => {
            const [amount, ...unit] = row.value.split(' ');
            return (
              <li key={row.uid}>
                <button
                  type="button"
                  className={styles.row}
                  data-hidden={row.hidden || undefined}
                  data-off-frame={
                    (!row.hidden && row.frame !== currentFrame) || undefined
                  }
                  aria-pressed={!row.hidden}
                  aria-label={`${row.isAI ? 'AI' : 'Manual'} ${row.label} ${row.value}`}
                  style={
                    {
                      '--accent': row.isAI
                        ? darkColors.annotationAiActive
                        : darkColors.annotationManualActive,
                    } as CSSProperties
                  }
                  onClick={() => toggle(row)}
                >
                  <span className={styles.label}>{row.label}</span>
                  <span className={styles.value}>
                    {amount}
                    <span className={styles.unit}>{unit.join(' ')}</span>
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
